import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { FileTypeEnum, ISecureFile, SVGFC } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import { DisplayImage } from '@repo/ui/file-input'
import { DeleteIcon, ImageIcon, WarningIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import Typography, { TextType } from '@repo/ui/typography'
import { forwardRef, type InputHTMLAttributes, ReactNode, useState } from 'react'

import { useSecureFile } from '../../../lib/hooks/use-secure-file'
import useTranslate from '../../../lib/hooks/use-translate'

export type SecureFileInputOptions = {
  displayDetails?: boolean
}

export interface SecureFileInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  types: FileTypeEnum[]
  placeholder: string
  options?: SecureFileInputOptions
  files?: ISecureFile[]
  hasError?: boolean
  label?: string
  accept?: string
  icon?: SVGFC
  iconContainerClassName?: string
  labelType?: TextType
  placeholderVariant?: TextType
  placeholderColor?: TailwindColor
  description?: ReactNode
  showDeleteIcon?: boolean
  allowedExtensions?: string[]
  previewOnClick?: boolean
  required?: boolean
  allowedAspectRatio?: `${number}:${number}`
  maxSize?: number
  onFilesChange: (files: ISecureFile[]) => void
  onNewFiles?: (files: File[]) => void
  onFileError?: (error: string) => void
}

const SHEET_EXTENSIONS = [
  'csv',
  'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'vnd.ms-excel'
]

export function getFileType(file: File): FileTypeEnum {
  const [fileType, extension] = file.type.split('/')

  if (SHEET_EXTENSIONS.includes(extension)) return FileTypeEnum.SHEET
  else if (fileType === 'application') return FileTypeEnum.PDF
  else if (fileType === 'video') return FileTypeEnum.VIDEO
  else if (fileType === 'text') return FileTypeEnum.XML
  else return FileTypeEnum.IMAGE
}

export function getAcceptAttribute(types: FileTypeEnum[], accept?: string): string {
  if (accept) return accept

  const acceptMap = {
    [FileTypeEnum.IMAGE]: 'image/*',
    [FileTypeEnum.VIDEO]: 'video/*',
    [FileTypeEnum.PDF]: 'application/pdf',
    [FileTypeEnum.XML]: 'application/xml',
    [FileTypeEnum.SHEET]:
      'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv'
  }

  return types.map((type) => acceptMap[type] || '').join(',') || '*'
}

function SecureFileItem({
  document,
  previewOnClick,
  onDelete
}: {
  document: ISecureFile
  previewOnClick?: boolean
  onDelete?: () => void
}) {
  const { url, isLoading, status } = useSecureFile(document.uploadId)
  const [showPreview, setShowPreview] = useState(false)
  const isError = status === 'INFECTED' || status === 'FAILED'

  return (
    <>
      <div className={'flex w-full items-center justify-between p-2 bg-grey-light rounded-lg'}>
        <div className={'flex flex-1 items-center gap-1 min-w-0'}>
          <div className={'h-12 w-12 relative shrink-0 flex items-center justify-center'}>
            {isLoading ? (
              <div className="h-12 w-12 rounded-sm bg-grey-light-hover flex items-center justify-center">
                <Loader size="xs" />
              </div>
            ) : isError ? (
              <div className="h-12 w-12 rounded-sm bg-red-50 flex items-center justify-center">
                <WarningIcon color={tailwindColors.secondaryRed.DEFAULT} />
              </div>
            ) : (
              <>
                {previewOnClick && (
                  <div
                    className={'cursor-pointer absolute z-40 h-12 w-12'}
                    onClick={() => {
                      if (document.type === FileTypeEnum.XML) window.open(url ?? '', '_blank')
                      else setShowPreview(true)
                    }}
                  />
                )}
                <DisplayImage
                  type={document.type}
                  src={url ?? ''}
                  className={'h-12 w-12 rounded-sm object-cover relative z-5 overflow-hidden'}
                />
              </>
            )}
          </div>

          <Typography variant={'tag'} color={tailwindColors.grey.DEFAULT} className={'break-all'}>
            {document.name}
          </Typography>
        </div>

        {onDelete && <ActionButton variant={'danger'} icon={DeleteIcon} onClick={onDelete} />}
      </div>

      {showPreview && (
        <Modal
          isOpen={showPreview}
          title={document.name}
          hideActions
          size={'large'}
          onCancel={() => setShowPreview(false)}
        >
          <DisplayImage
            allowAll
            type={document.type}
            src={url ?? ''}
            className={'h-[70vh] w-full rounded-sm object-contain relative z-10 overflow-auto'}
          />
        </Modal>
      )}
    </>
  )
}

export function DisplayFiles({
  files,
  previewOnClick,
  onDelete
}: {
  files?: ISecureFile[]
  previewOnClick?: boolean
  onDelete?: (files: ISecureFile[]) => void
}) {
  return (
    <div className={'flex flex-col items-center justify-start gap-2'}>
      {files?.map((document, index) => (
        <SecureFileItem
          key={index}
          document={document}
          previewOnClick={previewOnClick}
          onDelete={onDelete ? () => onDelete(files.filter((_, i) => i !== index)) : undefined}
        />
      ))}
    </div>
  )
}

const UploadSecureFileInput = forwardRef<HTMLInputElement, SecureFileInputProps>(
  (
    {
      className,
      hasError,
      labelType = 'tag',
      placeholderVariant = 'label',
      placeholderColor = tailwindColors.grey.DEFAULT,
      types,
      multiple,
      label,
      files,
      icon,
      iconContainerClassName,
      placeholder,
      accept,
      showDeleteIcon = true,
      previewOnClick,
      allowedExtensions,
      allowedAspectRatio,
      maxSize,
      required,
      options,
      onFilesChange,
      onNewFiles,
      onFileError,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslate()

    async function validateImage(file: File): Promise<boolean> {
      return new Promise((resolve) => {
        if (!types.includes(FileTypeEnum.IMAGE)) {
          resolve(true)
          return
        }
        const image = new Image()
        image.src = URL.createObjectURL(file)

        image.onload = () => {
          const { width, height } = image

          if (allowedAspectRatio) {
            const [allowedWidth, allowedHeight] = allowedAspectRatio.split(':').map(Number)

            // Calculate the actual aspect ratio of the image
            const actualAspectRatio = width / height
            const expectedAspectRatio = allowedWidth / allowedHeight

            // Check if the image's aspect ratio matches the allowed aspect ratio
            if (actualAspectRatio !== expectedAspectRatio) {
              onFileError?.(`${t('images.aspect.ratio.error')} ${allowedAspectRatio}`)
              resolve(false)
              return
            }
          }

          if (maxSize && file.size > maxSize) {
            onFileError?.(`${t('images.size.error')} ${maxSize / 1024 / 1024} Mo.`)
            resolve(false)
            return
          }

          resolve(true)
        }

        image.onerror = () => {
          onFileError?.(t('images.read.error'))
          resolve(false)
        }
      })
    }

    async function addImage(data: FileList | null) {
      const validFiles: File[] = []
      const newFiles = data ? Array.from(data) : []

      if (newFiles.length === 0) return

      for (const file of newFiles) {
        // Validate file extension
        if (allowedExtensions && !allowedExtensions.includes(file.name.split('.').pop() || '')) {
          onFileError?.(
            `Veuillez télécharger un fichier avec une extension valide ${allowedExtensions}`
          )
          continue
        }

        const type = getFileType(file)

        // Validate image dimensions
        if (type === FileTypeEnum.IMAGE) {
          const isValid = await validateImage(file)
          if (!isValid) continue // Skip this file if validation fails
        }

        validFiles.push(file)
      }

      // Emit validated native files for the parent to handle upload
      if (validFiles.length > 0) {
        if (!multiple) onNewFiles?.([validFiles[0]])
        else onNewFiles?.(validFiles)
      }
    }

    return (
      <div className="flex flex-col gap-2">
        <div className={'flex flex-col gap-2'}>
          {label && (
            <Typography
              variant={labelType}
              color={hasError ? tailwindColors.red.DEFAULT : tailwindColors.grey.DEFAULT}
            >
              {StringFormatter.isRequired(label, required)}
            </Typography>
          )}

          <div
            className={cn(
              'flex w-full px-1 h-40 flex-col relative items-center justify-center rounded-lg bg-transparent border border-dashed border-grey-light-active',
              { 'border-red': hasError },
              className
            )}
          >
            <input
              {...props}
              className={'absolute w-full h-full opacity-0'}
              ref={ref}
              accept={getAcceptAttribute(types, accept) ?? '*'}
              type={'file'}
              disabled={props.disabled || props.readOnly}
              onChange={(e) => addImage(e.target.files)}
              onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                const target = e.target as HTMLInputElement
                target.value = ''
              }}
              multiple={multiple}
              onDragOverCapture={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              onDrop={(e) => {
                e.preventDefault()
                addImage(e.dataTransfer.files)
              }}
            />
            <div
              className={
                '-z-100 flex h-full w-full flex-col items-center justify-center gap-3 rounded-lg p-2'
              }
            >
              <div className={cn('bg-primary-light p-4 rounded-3xl', iconContainerClassName)}>
                {icon ? (
                  icon({
                    color: tailwindColors.primary.DEFAULT,
                    className: 'h-7 w-7'
                  })
                ) : (
                  <ImageIcon className={'fill-primary h-7 w-7'} />
                )}
              </div>

              <Typography
                variant={placeholderVariant}
                className={'text-center'}
                color={placeholderColor}
              >
                {placeholder}
              </Typography>
              {options?.displayDetails && (
                <div className="flex flex-col justify-center items-center gap-1">
                  {allowedExtensions && allowedExtensions.length > 0 && (
                    <Typography variant="small" color={tailwindColors.grey.light.active}>
                      Téléchargez votre fichier uniquement au format&nbsp;
                      <span className="font-medium text-grey-light-active">
                        {allowedExtensions?.map((ext) => ext.toUpperCase())?.join(', ')}
                      </span>
                    </Typography>
                  )}
                  {maxSize && (
                    <Typography variant="small" color={tailwindColors.grey.light.active}>
                      Le fichier ne doit pas dépasser&nbsp;
                      <span className="font-medium text-grey-light-active">
                        {maxSize / 1024 / 1024} Mo
                      </span>
                    </Typography>
                  )}
                  {allowedAspectRatio && (
                    <Typography variant="small" color={tailwindColors.grey.light.active}>
                      Maintenez un rapport d'aspect de&nbsp;
                      <span className="font-medium text-grey-light-active">
                        {allowedAspectRatio}
                      </span>
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DisplayFiles
          files={files}
          previewOnClick={previewOnClick}
          onDelete={showDeleteIcon ? onFilesChange : undefined}
        />
      </div>
    )
  }
)

UploadSecureFileInput.displayName = 'Input File'

export { DisplayImage }

export default UploadSecureFileInput
